import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { GradedUnit } from 'app/library/graded-unit/models/graded-unit.model';
import { GradedUnitState } from 'app/library/graded-unit/models/graded-unit.state';
import { GradingScheme } from 'app/library/grading-scheme/models/grading-scheme.model';
import { GradingSchemeState } from 'app/library/grading-scheme/models/grading-scheme.state';
import { ModulePartialGradesState } from 'app/library/module-partial-grades/models/module-partial-grades.state';
import { AddPartialGrades } from 'app/library/partial-grade/actions';
import { PartialGrade } from 'app/library/partial-grade/models/partial-grade.model';
import { PartialGradeState } from 'app/library/partial-grade/models/partial-grade.state';
import { SubentityState } from 'app/projects/subentity/src/lib/models/subentity.state';
import {
    DeselectSelectedParticipantPartialGrades,
    FetchParticipantPartialGrades,
    FetchParticipantPartialGradesFailure,
    FetchParticipantPartialGradesSuccess,
    SearchParticipantPartialGrades,
    SelectFilteredParticipantPartialGrades,
    SetParticipantPartialGrades,
    ToggleSelectedParticipantPartialGrade,
    UnsetParticipantPartialGrades,
} from '../actions';
import { ParticipantPartialGradesService } from '../services/participant-partial-grades.service';
import { ParticipantPartialGradesStateModel } from './participant-partial-grades.state-model';

@State<ParticipantPartialGradesStateModel>({
    name: 'participantPartialGrades',
    defaults: new ParticipantPartialGradesStateModel(),
})
@Injectable()
export class ParticipantPartialGradesState extends SubentityState<PartialGrade> {
    @Selector([PartialGradeState.getEntities, GradingSchemeState.getEntity])
    static getEntities(state: ParticipantPartialGradesStateModel, partialGrades: PartialGrade[], gradingScheme: GradingScheme): PartialGrade[] {
        if (!partialGrades) {
            return null;
        }

        const toReturn = partialGrades.filter((partialGrade) => state.list.indexOf(partialGrade.id) !== -1);

        if (gradingScheme) {
            const gradesContainer = {};

            gradingScheme.grades.forEach((grade) => {
                gradesContainer[grade.id] = grade;
            });

            return toReturn.map<PartialGrade>((partialGrade) =>
                Object.assign<PartialGrade, PartialGrade, Partial<PartialGrade>>(new PartialGrade(), partialGrade, { grade: gradesContainer[partialGrade.gradeId] || null })
            );
        }

        return toReturn;
    }

    @Selector([PartialGradeState.getEntities, GradingSchemeState.getEntity, GradedUnitState.getEntities])
    static getEntitiesWithGradedUnit(
        state: ParticipantPartialGradesStateModel,
        partialGrades: PartialGrade[],
        gradingScheme: GradingScheme,
        gradedUnits: GradedUnit[]
    ): PartialGrade[] {
        let toReturn = ModulePartialGradesState.getEntities(state, partialGrades, gradingScheme);

        if (toReturn && gradedUnits) {
            const gradedUnitsContainer = {};

            gradedUnits.forEach((gradedUnit) => {
                gradedUnitsContainer[gradedUnit.id] = gradedUnit;
            });

            toReturn = toReturn.map<PartialGrade>((partialGrade) =>
                Object.assign<PartialGrade, PartialGrade, Partial<PartialGrade>>(new PartialGrade(), partialGrade, {
                    gradedUnit: gradedUnitsContainer[partialGrade.gradedUnitId] || null,
                })
            );

            toReturn.sort((a, b) => (a.gradedUnit.sequence > b.gradedUnit.sequence ? 1 : b.gradedUnit.sequence > a.gradedUnit.sequence ? -1 : 0));
        }

        return toReturn;
    }

    @Selector([PartialGradeState.getEntities, GradingSchemeState.getEntity])
    static getFilteredEntities(state: ParticipantPartialGradesStateModel, partialGrades: PartialGrade[], gradingScheme: GradingScheme): PartialGrade[] {
        if (!partialGrades) {
            return null;
        }

        const toReturn = partialGrades.filter((partialGrade) => state.filtered.indexOf(partialGrade.id) !== -1);

        if (gradingScheme) {
            const gradesContainer = {};

            gradingScheme.grades.forEach((grade) => {
                gradesContainer[grade.id] = grade;
            });

            return toReturn.map<PartialGrade>((partialGrade) =>
                Object.assign<PartialGrade, PartialGrade, Partial<PartialGrade>>(new PartialGrade(), partialGrade, { grade: gradesContainer[partialGrade.gradeId] || null })
            );
        }

        return toReturn;
    }

    @Selector([PartialGradeState.getEntities, GradingSchemeState.getEntity, GradedUnitState.getEntities])
    static getFilteredEntitiesWithGradedUnit(
        state: ParticipantPartialGradesStateModel,
        partialGrades: PartialGrade[],
        gradingScheme: GradingScheme,
        gradedUnits: GradedUnit[]
    ): PartialGrade[] {
        let toReturn = ModulePartialGradesState.getFilteredEntities(state, partialGrades, gradingScheme);

        if (toReturn && gradedUnits) {
            const gradedUnitsContainer = {};

            gradedUnits.forEach((gradedUnit) => {
                gradedUnitsContainer[gradedUnit.id] = gradedUnit;
            });

            toReturn = toReturn.map<PartialGrade>((partialGrade) =>
                Object.assign<PartialGrade, PartialGrade, Partial<PartialGrade>>(new PartialGrade(), partialGrade, {
                    gradedUnit: gradedUnitsContainer[partialGrade.participantId] || null,
                })
            );

            toReturn.sort((a, b) => (a.gradedUnit.sequence > b.gradedUnit.sequence ? 1 : b.gradedUnit.sequence > a.gradedUnit.sequence ? -1 : 0));
        }

        return toReturn;
    }

    @Selector()
    static getSelectedEntityIds(state: ParticipantPartialGradesStateModel): string[] {
        return state.selected;
    }

    constructor(private _gradedUnitPartialGradesService: ParticipantPartialGradesService, private store: Store) {
        super(null, 30);

        this._propstoSearch = [...PartialGrade.props_to_search];
    }

    @Action(SetParticipantPartialGrades)
    setAll(ctx: StateContext<ParticipantPartialGradesStateModel>, action: SetParticipantPartialGrades): void {
        this._setSubentities(ctx, action, AddPartialGrades);
    }

    @Action(UnsetParticipantPartialGrades)
    unsetAll(ctx: StateContext<ParticipantPartialGradesStateModel>): void {
        this._unsetSubentities(ctx);
    }

    @Action(FetchParticipantPartialGrades)
    fetchAll(ctx: StateContext<ParticipantPartialGradesStateModel>, action: FetchParticipantPartialGrades): void {
        this._fetchSubentities(
            ctx,
            action,
            SetParticipantPartialGrades,
            FetchParticipantPartialGradesSuccess,
            FetchParticipantPartialGradesFailure,
            this._gradedUnitPartialGradesService.getAll(action.payload)
        );
    }

    @Action(FetchParticipantPartialGradesSuccess)
    fetchAllSuccess(ctx: StateContext<ParticipantPartialGradesStateModel>): void {
        this._fetchSubentitiesSuccess(ctx);
    }

    @Action(FetchParticipantPartialGradesFailure)
    fetchAllFailure(ctx: StateContext<ParticipantPartialGradesStateModel>): void {
        this._fetchSubentitiesFailure(ctx);
    }

    @Action(ToggleSelectedParticipantPartialGrade)
    toggleSelect(ctx: StateContext<ParticipantPartialGradesStateModel>, action: ToggleSelectedParticipantPartialGrade): void {
        this._toggleSelectedSubentity(ctx, action);
    }

    @Action(SelectFilteredParticipantPartialGrades)
    selectAll(ctx: StateContext<ParticipantPartialGradesStateModel>): void {
        this._selectFilteredSubentities(ctx);
    }

    @Action(DeselectSelectedParticipantPartialGrades)
    deselectAll(ctx: StateContext<ParticipantPartialGradesStateModel>): void {
        this._deselectSelectedSubentities(ctx);
    }

    @Action(SearchParticipantPartialGrades)
    search(ctx: StateContext<ParticipantPartialGradesStateModel>, action: SearchParticipantPartialGrades): void {
        const partialGrades = this.store.selectSnapshot(PartialGradeState.getEntities);

        this._searchSubentities(ctx, action, partialGrades);
    }
}
